import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  UserCheck, Mail, BookOpen, Settings, Sparkles, 
  CheckCircle2, Clock, Send, Edit2, Save
} from "lucide-react";
import { useDeals, Deal } from "@/hooks/useDeals";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  delay: string;
  enabled: boolean;
  template?: string;
}

const DEFAULT_ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome Email',
    description: 'Send personalized welcome message with account details',
    icon: Mail,
    delay: 'Immediate',
    enabled: true,
    template: `Hi {{customer_name}},

Welcome to {{company_name}}! We're thrilled to have you on board.

Your account has been set up and you're ready to get started. Here's what you can do next:

1. Log in to your dashboard
2. Complete your profile setup
3. Explore our getting started guide

If you have any questions, our support team is here to help.

Best regards,
The {{company_name}} Team`,
  },
  {
    id: 'setup',
    title: 'Setup Instructions',
    description: 'Guide customers through initial configuration',
    icon: Settings,
    delay: '+1 day',
    enabled: true,
    template: `Hi {{customer_name}},

Ready to set up your account? Here's a quick guide to get you started:

Step 1: Configure your preferences
Step 2: Import your existing data
Step 3: Invite your team members
Step 4: Set up integrations

Need help? Reply to this email or check our documentation.

Best,
{{agent_name}}`,
  },
  {
    id: 'training',
    title: 'Training Resources',
    description: 'Share training videos and documentation',
    icon: BookOpen,
    delay: '+3 days',
    enabled: true,
    template: `Hi {{customer_name}},

We've put together some resources to help you get the most out of {{company_name}}:

📹 Video Tutorials: [Link]
📚 Documentation: [Link]
💡 Best Practices Guide: [Link]
🎓 Webinar Schedule: [Link]

Investing a little time in learning will help you achieve amazing results!

Best,
{{agent_name}}`,
  },
  {
    id: 'checkin',
    title: 'Check-in Call',
    description: 'Schedule a success check-in call',
    icon: UserCheck,
    delay: '+7 days',
    enabled: true,
    template: `Hi {{customer_name}},

It's been a week since you joined us! I'd love to schedule a quick call to:

- See how things are going
- Answer any questions
- Share tips based on your usage

Would any of these times work for a 15-minute call?
[Calendar Link]

Looking forward to connecting!

Best,
{{agent_name}}`,
  },
];

export function CustomerOnboardingAutomation() {
  const { toast } = useToast();
  const { data: deals } = useDeals();
  const [steps, setSteps] = useState<OnboardingStep[]>(DEFAULT_ONBOARDING_STEPS);
  const [editingStep, setEditingStep] = useState<string | null>(null);
  const [editedTemplate, setEditedTemplate] = useState<string>('');

  // Get recently won deals (potential onboarding candidates)
  const wonDeals = deals?.filter(d => d.status === 'won')
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 5) || [];

  const toggleStep = (stepId: string) => {
    setSteps(prev => prev.map(s => 
      s.id === stepId ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const startEditing = (step: OnboardingStep) => {
    setEditingStep(step.id);
    setEditedTemplate(step.template || '');
  };

  const saveTemplate = (stepId: string) => {
    setSteps(prev => prev.map(s => 
      s.id === stepId ? { ...s, template: editedTemplate } : s
    ));
    setEditingStep(null);
    toast({
      title: "Template Saved",
      description: "Onboarding email template has been updated.",
    });
  };

  const triggerOnboarding = (deal: any) => {
    toast({
      title: "Onboarding Started",
      description: `Automated onboarding sequence initiated for ${deal.title}`,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <UserCheck className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Customer Onboarding Automation</h2>
            <p className="text-sm text-muted-foreground">
              Automated welcome emails, setup guides, and training resources
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Onboarding Steps Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Onboarding Sequence
            </CardTitle>
            <CardDescription>
              Configure automated onboarding steps for new customers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div 
                  key={step.id}
                  className={cn(
                    "p-4 rounded-lg border transition-all",
                    step.enabled ? "bg-card" : "bg-muted/30 opacity-60"
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "p-2 rounded-lg",
                        step.enabled ? "bg-primary/10" : "bg-muted"
                      )}>
                        <Icon className={cn(
                          "h-4 w-4",
                          step.enabled ? "text-primary" : "text-muted-foreground"
                        )} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{step.title}</span>
                          <Badge variant="outline" className="text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {step.delay}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {step.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => startEditing(step)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Switch 
                        checked={step.enabled}
                        onCheckedChange={() => toggleStep(step.id)}
                      />
                    </div>
                  </div>

                  {editingStep === step.id && (
                    <div className="mt-4 space-y-3">
                      <Label>Email Template</Label>
                      <Textarea 
                        value={editedTemplate}
                        onChange={(e) => setEditedTemplate(e.target.value)}
                        rows={8}
                        className="font-mono text-sm"
                      />
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditingStep(null)}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={() => saveTemplate(step.id)}>
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Recent Won Deals */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Ready for Onboarding
            </CardTitle>
            <CardDescription>
              Recently won deals awaiting customer onboarding
            </CardDescription>
          </CardHeader>
          <CardContent>
            {wonDeals.length > 0 ? (
              <div className="space-y-3">
                {wonDeals.map(deal => (
                  <div 
                    key={deal.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div>
                      <div className="font-medium">{deal.title}</div>
                      <div className="text-sm text-muted-foreground">
                        ${deal.amount?.toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Won on {new Date(deal.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => triggerOnboarding(deal)}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Start Onboarding
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <UserCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No recently won deals</p>
                <p className="text-sm">Win some deals to see them here!</p>
              </div>
            )}

            {/* Onboarding Stats */}
            <div className="mt-6 pt-6 border-t">
              <h4 className="text-sm font-medium mb-3">Onboarding Stats</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{wonDeals.length}</div>
                  <div className="text-xs text-muted-foreground">Pending</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-500">{steps.filter(s => s.enabled).length}</div>
                  <div className="text-xs text-muted-foreground">Active Steps</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-500">95%</div>
                  <div className="text-xs text-muted-foreground">Success Rate</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
